import React from 'react';
import { ArrowUpRight } from 'lucide-react';
import { businessInfo } from '../data/mock';

const HERO_IMG =
  'https://images.unsplash.com/photo-1590042296398-01db0e07de68?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1600';
const HERO_IMG_SIDE =
  'https://images.unsplash.com/photo-1767972463246-aaf13f2ad8a1?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=900';

const Hero = () => {
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative pt-28 md:pt-32 pb-28 md:pb-32 bg-onyx overflow-hidden grain"
    >
      {/* Soft radial glow */}
      <div
        aria-hidden="true"
        className="absolute -top-32 -left-40 w-[520px] h-[520px] rounded-full blur-3xl opacity-[0.18]"
        style={{ background: 'radial-gradient(circle, #D1679A 0%, transparent 60%)' }}
      />
      <div
        aria-hidden="true"
        className="absolute top-1/2 -right-24 w-[420px] h-[420px] rounded-full blur-3xl opacity-[0.12]"
        style={{ background: 'radial-gradient(circle, #EC8AAE 0%, transparent 60%)' }}
      />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* Copy */}
          <div className="lg:col-span-7 rise">
            <p className="eyebrow mb-6" data-testid="hero-eyebrow">
              <span className="inline-block w-8 h-px bg-silverx align-middle mr-3" />
              Luxury Body Sculpting &amp; <span className="brand-italic normal-case tracking-normal chrome-rose ml-1">Wellness</span>
            </p>

            <h1 className="font-serif text-ivory text-5xl sm:text-6xl lg:text-[88px] leading-[1.06] sm:leading-[1.02] lg:leading-[0.98] tracking-tight mb-8 text-balance">
              Sculpt. <span className="brand-italic chrome-rose">Tighten.</span>
              <br />
              Transform.
            </h1>

            <p className="text-platinum text-base sm:text-lg max-w-xl leading-relaxed mb-10">
              Elite non-invasive body contouring, lymphatic renewal, and recovery
              rituals—delivered through an elevated, private boutique experience.
            </p>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 mb-14">
              <a
                href={businessInfo.bookingUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-book-btn"
                className="btn-pill group inline-flex items-center gap-3 px-8 py-4 rounded-full bg-ivory text-onyx text-[11px] tracking-[0.28em] uppercase font-medium hover:bg-blush"
              >
                Book Consultation
                <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </a>
              <a
                href="#services"
                data-testid="hero-explore-btn"
                className="btn-pill inline-flex items-center gap-3 px-8 py-4 rounded-full border border-pewter text-ivory text-[11px] tracking-[0.28em] uppercase font-medium hover:border-blush hover:text-blush"
              >
                Explore Treatments
              </a>
            </div>

            <p className="brand-italic text-silverx text-sm max-w-md">
              Curated treatments. Elevated experience. Visible results.
            </p>
          </div>

          {/* Editorial image stack */}
          <div className="lg:col-span-5 relative rise rise-delay-2">
            <div className="relative aspect-[4/5] rounded-sm overflow-hidden border border-pewter/70">
              <img
                src={HERO_IMG}
                alt="Sculpted silhouette editorial"
                className="absolute inset-0 w-full h-full object-cover editorial-img"
                loading="eager"
              />
              <div
                aria-hidden="true"
                className="absolute inset-0"
                style={{
                  background:
                    'linear-gradient(180deg, rgba(10,10,11,0) 55%, rgba(10,10,11,0.55) 100%)',
                }}
              />
              <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between">
                <div>
                  <p className="text-[10px] tracking-[0.3em] uppercase chrome-rose mb-1">
                    Signature Contour
                  </p>
                  <p className="font-serif text-ivory text-lg leading-tight">
                    Sculpted. Refined. <span className="brand-italic chrome-rose">Elevated.</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Secondary body contour sliver */}
            <div className="hidden md:block absolute -bottom-10 -left-10 w-40 h-52 rounded-sm overflow-hidden border border-pewter/70 shadow-2xl">
              <img
                src={HERO_IMG_SIDE}
                alt="Torso sculpting detail"
                className="w-full h-full object-cover editorial-img"
                loading="lazy"
              />
            </div>
          </div>
        </div>

        {/* Trust Bar */}
        <div className="mt-20 pt-10 border-t border-pewter/60 grid grid-cols-2 sm:grid-cols-4 gap-8" data-testid="hero-trust-bar">
          {[
            ['Non-Invasive', 'Zero downtime'],
            ['Certified', 'Licensed & Insured'],
            ['Concierge', 'Private luxury setting'],
            ['Education-first', 'Transparent guidance'],
          ].map(([title, sub]) => (
            <div key={title}>
              <p className="font-serif text-ivory text-xl md:text-2xl">{title}</p>
              <p className="text-silverx text-xs tracking-[0.2em] uppercase mt-2">{sub}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;
